import { useState } from "react";
import { missingRequiredPlantSlots, plantPacketReadyToSubmit } from "@/lib/ops/plantPacketComplete";
import { isPlantPacketItem } from "@/lib/ops/plantPacket";
import { submitOpsDealToBuild } from "@/lib/opsPaperworkClient";
import type { DealLinkedUnit, PaperItem } from "./opsPaperworkTypes";

export function OpsDealSubmitToBuildCard(props: {
  dealId: string;
  deal: Record<string, unknown>;
  items: PaperItem[];
  attachmentCounts: ReadonlyMap<string, number>;
  linkedUnit: DealLinkedUnit | null;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const plantItems = props.items.filter((row) => isPlantPacketItem(row));
  const missing = missingRequiredPlantSlots(plantItems, props.attachmentCounts);
  const ready = plantPacketReadyToSubmit(plantItems, props.attachmentCounts);
  const submittedAt =
    typeof props.deal.submitted_to_build_at === "string" ? props.deal.submitted_to_build_at : null;
  const hasUnit = Boolean(props.linkedUnit);

  if (!hasUnit) {
    return (
      <section className="h-full rounded-lg border border-gray-200 p-3 space-y-1 dark:border-zinc-700">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-zinc-100">Submit to build</h3>
        <p className="text-xs text-gray-500">
          No in-app manufacturer unit on this deal. Vendor-resold / inventory sales skip the plant Kanban.
        </p>
      </section>
    );
  }

  async function submit() {
    setBusy(true);
    setErr("");
    const r = await submitOpsDealToBuild(props.dealId);
    setBusy(false);
    if (!r.ok) {
      setErr(r.error);
      return;
    }
    await props.onChanged();
  }

  return (
    <section className="h-full rounded-lg border border-indigo-200 p-3 space-y-2 dark:border-indigo-900">
      <h3 className="text-sm font-semibold text-gray-800 dark:text-zinc-100">Submit to build</h3>
      <p className="text-xs text-gray-600 dark:text-zinc-300">
        Retail and plant ops confirm this org’s paperwork and the manufacturer plant packet, then send the unit
        to Order approved. Ready to deliver stays off until Final QC.
      </p>
      {submittedAt ? (
        <p className="text-xs text-green-800 dark:text-green-300">
          Submitted {new Date(submittedAt).toLocaleString()}
          {props.linkedUnit?.serial ? ` · serial ${props.linkedUnit.serial}` : ""}
          {props.linkedUnit?.current_phase_name ? ` · ${props.linkedUnit.current_phase_name}` : ""}
        </p>
      ) : null}
      {!ready ? (
        <p className="text-xs text-amber-800 dark:text-amber-200">
          Still needed: {missing.map((m) => m.label || m.item_key).join(", ") || "required plant uploads"}
        </p>
      ) : (
        <p className="text-xs text-gray-600 dark:text-zinc-300">Plant packet required slots are complete.</p>
      )}
      <button
        type="button"
        className="rounded-md bg-indigo-700 px-3 py-1.5 text-sm text-white disabled:opacity-50 dark:bg-indigo-500"
        disabled={busy || !ready}
        onClick={() => void submit()}
      >
        {submittedAt ? "Re-check / submit again" : "Submit to build"}
      </button>
      {err ? <p className="text-xs text-red-700">{err}</p> : null}
    </section>
  );
}
